import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Search, Eye, Star, StarOff, ToggleLeft, ToggleRight,
  Users, MessageSquare, ChevronLeft, RefreshCw, AlertCircle,
  Globe, Lock, UserPlus, MapPin, BarChart3, X, Plus,
  Image, Pin, EyeOff, Hash, Upload, Edit2
} from 'lucide-react';
import {
  getAllChannels,
  getChannelMembers,
  getChannelPosts,
  moderatePost,
  featureChannel,
  toggleChannelActive,
  getChannelAnalytics,
  createChannel,
  updateChannel,
  searchUsers
} from '../../utils/channelsAdminApi';
import { uploadChannelImage, deleteChannelImage, validateFile } from '../../utils/cloudinaryUpload';
import '../../styles/ChannelsAdmin.css';

// ─── Module-level utilities ────────────────────────────────────────────────────
// Defined outside any component so their references never change.

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const PrivacyIcon = ({ privacy }) => {
  if (privacy === 'private') return <Lock size={13} />;
  if (privacy === 'invite_only') return <UserPlus size={13} />;
  return <Globe size={13} />;
};

const CreatorDisplay = ({ channel }) => {
  const creator = channel.users;
  if (channel.is_platform_created) {
    return (
      <span className="creator-display">
        <span className="creator-platform-tag">VibrantCast</span>
        {creator && (
          <span className="creator-name" style={{ color: 'var(--admin-gray-400)', fontSize: '0.75rem' }}>
            (via @{creator.handle})
          </span>
        )}
      </span>
    );
  }
  if (!creator) return <span className="creator-name">—</span>;
  return (
    <span className="creator-display">
      {creator.profile_img
        ? <img src={creator.profile_img} alt="" className="creator-avatar" />
        : <span className="creator-avatar" />
      }
      <span className="creator-name">@{creator.handle}</span>
    </span>
  );
};

// ─── AnalyticsTab ──────────────────────────────────────────────────────────────

const AnalyticsTab = ({ analytics }) => {
  if (!analytics) {
    return (
      <div className="channels-loading">
        <div className="loading-spinner" />
        <span>Loading analytics…</span>
      </div>
    );
  }
  const cards = [
    { label: 'Total Channels', value: analytics.totalChannels, icon: <Hash size={22} />, color: 'blue' },
    { label: 'Active Channels', value: analytics.activeChannels, icon: <ToggleRight size={22} />, color: 'green' },
    { label: 'Featured', value: analytics.featuredChannels, icon: <Star size={22} />, color: 'gold' },
    { label: 'Total Members', value: analytics.totalMembers, icon: <Users size={22} />, color: 'purple' },
    { label: 'Total Posts', value: analytics.totalPosts, icon: <MessageSquare size={22} />, color: 'teal' },
    { label: 'New (Last 7 Days)', value: analytics.newChannels, icon: <BarChart3 size={22} />, color: 'orange' }
  ];
  return (
    <div className="channel-analytics">
      {cards.map(card => (
        <div className="analytics-card" key={card.label}>
          <div className={`stat-icon-wrapper ${card.color}`}>{card.icon}</div>
          <div className="stat-body">
            <div className="stat-value">{card.value}</div>
            <div className="stat-label">{card.label}</div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── MembersTab ────────────────────────────────────────────────────────────────

const MembersTab = ({ members }) => {
  if (!members.length) {
    return (
      <div className="empty-state">
        <Users size={36} />
        <p>No members found.</p>
      </div>
    );
  }
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="channel-members-table">
        <thead>
          <tr>
            <th>Member</th>
            <th>Email</th>
            <th>Role</th>
            <th>Status</th>
            <th>Joined</th>
          </tr>
        </thead>
        <tbody>
          {members.map(member => {
            const user = member.users;
            return (
              <tr key={member.id}>
                <td>
                  <div className="member-cell">
                    {user?.profile_img
                      ? <img src={user.profile_img} alt="" className="member-avatar" />
                      : <span className="member-avatar">{user?.nickname?.[0] || '?'}</span>
                    }
                    <div>
                      <div className="member-name">{user?.nickname || '—'}</div>
                      <div className="member-handle">@{user?.handle || '—'}</div>
                    </div>
                  </div>
                </td>
                <td style={{ fontSize: '0.82rem', color: 'var(--admin-gray-600)' }}>{user?.email || '—'}</td>
                <td><span className={`role-badge ${member.role}`}>{member.role}</span></td>
                <td><span className={`status-badge ${member.status}`}>{member.status}</span></td>
                <td style={{ fontSize: '0.82rem', color: 'var(--admin-gray-500)' }}>{formatDate(member.joined_at)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ─── PostsTab ──────────────────────────────────────────────────────────────────

const PostsTab = ({ posts, pagination, onModerate, onPageChange }) => {
  if (!posts.length) {
    return (
      <div className="empty-state">
        <MessageSquare size={36} />
        <p>No posts found.</p>
      </div>
    );
  }
  const currentPage = Math.floor(pagination.offset / pagination.limit) + 1;
  const totalPages = Math.ceil(pagination.total / pagination.limit);
  return (
    <>
      <div className="channel-posts-list">
        {posts.map(post => {
          const author = post.users;
          return (
            <div className="channel-post-item" key={post.id} style={{ opacity: post.is_active ? 1 : 0.6 }}>
              <div className="post-item-header">
                <div className="post-author">
                  {author?.profile_img
                    ? <img src={author.profile_img} alt="" className="post-author-avatar" />
                    : <span className="post-author-avatar">{author?.nickname?.[0] || '?'}</span>
                  }
                  <div>
                    <div className="post-author-name">{author?.nickname || '—'}</div>
                    <div className="post-author-handle">@{author?.handle || '—'}</div>
                  </div>
                </div>
                <div className="post-meta-badges">
                  <span className="post-type-badge">{post.post_type}</span>
                  {post.pinned && <span className="pinned-badge"><Pin size={11} /> Pinned</span>}
                  {!post.is_active && (
                    <span className="post-hidden-indicator"><EyeOff size={12} /> Hidden</span>
                  )}
                </div>
              </div>
              {post.content && <div className="post-content">{post.content}</div>}
              {post.media_url && post.media_type === 'image' && (
                <img src={post.media_url} alt="post media" className="post-media-thumb" />
              )}
              {post.media_url && post.media_type === 'video' && (
                <div className="post-media-thumb" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Image size={24} color="var(--admin-gray-400)" />
                </div>
              )}
              <div className="post-item-footer">
                <span className="post-date">{formatDate(post.created_at)}</span>
                <button
                  className={`btn-moderate ${post.is_active ? 'hide' : 'unhide'}`}
                  onClick={() => onModerate(post)}
                >
                  {post.is_active ? <><EyeOff size={13} /> Hide</> : <><Eye size={13} /> Unhide</>}
                </button>
              </div>
            </div>
          );
        })}
      </div>
      {pagination.total > pagination.limit && (
        <div className="channels-pagination" style={{ marginTop: 16 }}>
          <span>Page {currentPage} of {totalPages} ({pagination.total} posts)</span>
          <button
            disabled={pagination.offset === 0}
            onClick={() => onPageChange(pagination.offset - pagination.limit)}
          >
            Previous
          </button>
          <button
            disabled={pagination.offset + pagination.limit >= pagination.total}
            onClick={() => onPageChange(pagination.offset + pagination.limit)}
          >
            Next
          </button>
        </div>
      )}
    </>
  );
};

// ─── ChannelDetailView ────────────────────────────────────────────────────────

const ChannelDetailView = ({
  channel,
  members,
  posts,
  postsPagination,
  detailTab,
  onBack,
  onTabChange,
  onToggleFeatured,
  onToggleActive,
  onModeratePost,
  onLoadMorePosts,
  onEdit
}) => {
  const c = channel;
  return (
    <div className="channel-detail">
      <button className="channel-detail-back" onClick={onBack}>
        <ChevronLeft size={16} /> Back to Channels
      </button>

      {/* Header */}
      <div className="channel-detail-header">
        <div className="channel-detail-header-inner">
          <div className="channel-cover-image">
            {c.cover_image
              ? <img src={c.cover_image} alt={c.name} />
              : <Image size={32} />
            }
          </div>
          <div className="channel-detail-meta">
            <h2>{c.name}</h2>
            <div className="detail-slug">/{c.slug}</div>
            <div className="badge-row">
              <span className={`type-badge ${c.channel_type}`}>{c.channel_type}</span>
              <span className={`privacy-badge ${c.privacy}`}>
                <PrivacyIcon privacy={c.privacy} /> {c.privacy.replace('_', ' ')}
              </span>
              {c.is_platform_created && (
                <span className="platform-created-badge">VibrantCast</span>
              )}
              {c.is_featured && (
                <span style={{ color: '#854d0e', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 3 }}>
                  <Star size={13} fill="currentColor" /> Featured
                </span>
              )}
            </div>
            <div className="stat-row">
              <span><Users size={13} /> {c.member_count ?? 0} members</span>
              <span>Created {formatDate(c.created_at)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Action row */}
      <div className="detail-actions">
        <button
          className={`btn-feature ${c.is_featured ? 'featured' : ''}`}
          onClick={() => onToggleFeatured(c)}
        >
          {c.is_featured ? <><StarOff size={15} /> Unfeature</> : <><Star size={15} /> Feature</>}
        </button>
        <button className="btn-edit" onClick={onEdit}>
          <Edit2 size={15} /> Edit
        </button>
        <button className="btn-toggle-active" onClick={() => onToggleActive(c)}>
          {c.is_active
            ? <><ToggleLeft size={15} /> Deactivate</>
            : <><ToggleRight size={15} /> Activate</>
          }
        </button>
      </div>

      {/* Inner tabs */}
      <div className="channel-detail-tabs">
        {[
          { id: 'overview', label: 'Overview' },
          { id: 'members', label: `Members (${members.length})` },
          { id: 'posts', label: `Posts (${postsPagination.total})` }
        ].map(tab => (
          <button
            key={tab.id}
            className={detailTab === tab.id ? 'active' : ''}
            onClick={() => onTabChange(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="channel-detail-content">
        {detailTab === 'overview' && (
          <>
            {c.description && (
              <div className="overview-section">
                <h4>Description</h4>
                <p>{c.description}</p>
              </div>
            )}

            {c.tags && c.tags.length > 0 && (
              <div className="overview-section">
                <h4>Tags</h4>
                <div className="tags-list">
                  {c.tags.map((tag, i) => (
                    <span key={i} className="tag-chip">{tag}</span>
                  ))}
                </div>
              </div>
            )}

            {c.channel_type === 'location' && (c.location_city || c.location_region) && (
              <div className="overview-section">
                <h4>Location</h4>
                <p style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <MapPin size={14} />
                  {[c.location_city, c.location_region].filter(Boolean).join(', ')}
                </p>
              </div>
            )}

            <div className="overview-section">
              <h4>Creator</h4>
              {c.is_platform_created ? (
                <>
                  <div style={{ marginBottom: 8 }}>
                    <span className="platform-created-badge"><Hash size={12} /> Created by VibrantCast</span>
                  </div>
                  {c.users && (
                    <p style={{ fontSize: '0.85rem', color: 'var(--admin-gray-500)' }}>
                      Internal owner: @{c.users.handle} ({c.users.email})
                    </p>
                  )}
                </>
              ) : (
                c.users ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {c.users.profile_img
                      ? <img src={c.users.profile_img} alt="" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }} />
                      : <span style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--admin-gray-300)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem' }}>{c.users.nickname?.[0]}</span>
                    }
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{c.users.nickname}</div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--admin-gray-500)' }}>@{c.users.handle} · {c.users.email}</div>
                    </div>
                  </div>
                ) : <p style={{ fontSize: '0.875rem', color: 'var(--admin-gray-500)' }}>—</p>
              )}
            </div>
          </>
        )}

        {detailTab === 'members' && <MembersTab members={members} />}
        {detailTab === 'posts' && (
          <PostsTab
            posts={posts}
            pagination={postsPagination}
            onModerate={onModeratePost}
            onPageChange={onLoadMorePosts}
          />
        )}
      </div>
    </div>
  );
};

// ─── CreateChannelModal ──────────────────────────────────────────────────────

const CreateChannelModal = ({
  ownerSearchQuery,
  ownerSearchResults,
  selectedOwner,
  createForm,
  createLoading,
  createError,
  coverImagePreview,
  onOwnerSearchChange,
  onSelectOwner,
  onClearOwner,
  onFormChange,
  onCoverImageChange,
  onCoverImageRemove,
  onSubmit,
  onClose
}) => {
  const fileInputRef = React.useRef(null);
  return (
  <div
    className="modal-overlay"
    onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
  >
    <div className="modal-content large create-channel-modal">
      <div className="modal-header">
        <h3>Create Channel</h3>
        <button onClick={onClose}><X size={18} /></button>
      </div>

      <div className="modal-body">
        {/* Owner search */}
        <div className="form-group">
          <label>Owner <span className="required">*</span></label>
          {selectedOwner ? (
            <div className="owner-selected-chip">
              {selectedOwner.profile_img
                ? <img src={selectedOwner.profile_img} alt="" className="owner-chip-avatar" />
                : <span className="owner-chip-avatar" />
              }
              <div>
                <div className="owner-chip-name">{selectedOwner.nickname}</div>
                <div className="owner-chip-handle">@{selectedOwner.handle}</div>
              </div>
              <button className="owner-chip-remove" onClick={onClearOwner}><X size={14} /></button>
            </div>
          ) : (
            <div className="owner-search-wrapper">
              <input
                type="text"
                className="owner-search-input"
                placeholder="Search by name, handle, or email…"
                value={ownerSearchQuery}
                onChange={(e) => onOwnerSearchChange(e.target.value)}
                autoComplete="off"
              />
              {ownerSearchResults.length > 0 && (
                <div className="owner-search-results">
                  {ownerSearchResults.map(user => (
                    <div
                      key={user.uuid}
                      className="owner-search-result-item"
                      onClick={() => onSelectOwner(user)}
                    >
                      {user.profile_img
                        ? <img src={user.profile_img} alt="" className="owner-result-avatar" />
                        : <span className="owner-result-avatar">{user.nickname?.[0] || '?'}</span>
                      }
                      <div>
                        <div className="owner-result-name">{user.nickname}</div>
                        <div className="owner-result-handle">@{user.handle}</div>
                        <div className="owner-result-email">{user.email}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Name */}
        <div className="form-group">
          <label>Name <span className="required">*</span></label>
          <input
            type="text"
            className="form-input"
            maxLength={100}
            placeholder="Channel name"
            value={createForm.name}
            onChange={(e) => onFormChange('name', e.target.value)}
          />
        </div>

        {/* Description */}
        <div className="form-group">
          <label>Description</label>
          <textarea
            className="form-textarea"
            maxLength={500}
            placeholder="Optional description…"
            value={createForm.description}
            onChange={(e) => onFormChange('description', e.target.value)}
          />
        </div>

        {/* Cover Image */}
        <div className="form-group">
          <label>Cover Image</label>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            style={{ display: 'none' }}
            onChange={onCoverImageChange}
          />
          {coverImagePreview ? (
            <div className="cover-image-picker cover-image-picker--has-preview">
              <img src={coverImagePreview} alt="Cover preview" className="cover-image-preview" />
              <div className="cover-image-picker-actions">
                <button
                  type="button"
                  className="cover-image-btn"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload size={13} /> Change
                </button>
                <button
                  type="button"
                  className="cover-image-btn cover-image-btn--remove"
                  onClick={onCoverImageRemove}
                >
                  <X size={13} /> Remove
                </button>
              </div>
            </div>
          ) : (
            <div
              className="cover-image-picker"
              onClick={() => fileInputRef.current?.click()}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
            >
              <Image size={22} color="var(--admin-gray-400)" />
              <span className="cover-image-picker-label">Click to upload cover image</span>
              <span className="cover-image-picker-hint">JPG, PNG, WebP · max 10 MB</span>
            </div>
          )}
        </div>

        {/* Type + Privacy */}
        <div className="form-row">
          <div className="form-group">
            <label>Type</label>
            <select
              className="form-select"
              value={createForm.channel_type}
              onChange={(e) => onFormChange('channel_type', e.target.value)}
            >
              <option value="interest">Interest</option>
              <option value="location">Location</option>
              <option value="organization">Organization</option>
            </select>
          </div>
          <div className="form-group">
            <label>Privacy</label>
            <select
              className="form-select"
              value={createForm.privacy}
              onChange={(e) => onFormChange('privacy', e.target.value)}
            >
              <option value="public">Public</option>
              <option value="private">Private</option>
              <option value="invite_only">Invite Only</option>
            </select>
          </div>
        </div>

        {/* Location fields (only when type = location) */}
        {createForm.channel_type === 'location' && (
          <div className="form-row">
            <div className="form-group">
              <label>City</label>
              <input
                type="text"
                className="form-input"
                placeholder="City"
                value={createForm.location_city}
                onChange={(e) => onFormChange('location_city', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Region / State</label>
              <input
                type="text"
                className="form-input"
                placeholder="Region or state"
                value={createForm.location_region}
                onChange={(e) => onFormChange('location_region', e.target.value)}
              />
            </div>
          </div>
        )}

        {/* Tags */}
        <div className="form-group">
          <label>Tags</label>
          <input
            type="text"
            className="form-input"
            placeholder="Comma-separated tags (e.g. painting, sculpture, abstract)"
            value={createForm.tags}
            onChange={(e) => onFormChange('tags', e.target.value)}
          />
        </div>

        {/* Featured */}
        <div className="form-group">
          <label className="form-checkbox-group">
            <input
              type="checkbox"
              checked={createForm.is_featured}
              onChange={(e) => onFormChange('is_featured', e.target.checked)}
            />
            <div>
              <div className="checkbox-label">Featured channel</div>
              <div className="checkbox-hint">Appears in featured channel lists in the app</div>
            </div>
          </label>
        </div>

        {/* Platform created */}
        <div className="form-group">
          <label className="form-checkbox-group">
            <input
              type="checkbox"
              checked={createForm.is_platform_created}
              onChange={(e) => onFormChange('is_platform_created', e.target.checked)}
            />
            <div>
              <div className="checkbox-label">Created by VibrantCast</div>
              <div className="checkbox-hint">
                Shows "Created by VibrantCast" publicly instead of the owner's profile. Owner is still required for permissions.
              </div>
            </div>
          </label>
        </div>

        {createError && (
          <div className="form-error">
            <AlertCircle size={15} />
            {createError}
          </div>
        )}
      </div>

      <div className="modal-footer">
        <button className="btn-cancel" onClick={onClose} disabled={createLoading}>
          Cancel
        </button>
        <button className="btn-submit" onClick={onSubmit} disabled={createLoading}>
          {createLoading ? 'Creating…' : 'Create Channel'}
        </button>
      </div>
    </div>
  </div>
  );
};

// ─── EditChannelModal ────────────────────────────────────────────────────────

const EditChannelModal = ({ channel, loading, error, onSubmit, onClose }) => {
  const [form, setForm] = React.useState({
    name: channel.name || '',
    description: channel.description || '',
    channel_type: channel.channel_type || 'interest',
    privacy: channel.privacy || 'public',
    location_city: channel.location_city || '',
    location_region: channel.location_region || '',
    tags: (channel.tags || []).join(', '),
    is_featured: channel.is_featured || false,
    is_platform_created: channel.is_platform_created || false,
  });
  const [coverImageFile, setCoverImageFile] = React.useState(null);
  const [coverImagePreview, setCoverImagePreview] = React.useState(channel.cover_image || null);
  const [coverRemoved, setCoverRemoved] = React.useState(false);
  const [fileError, setFileError] = React.useState(null);
  const fileInputRef = React.useRef(null);

  // Revoke blob URLs when coverImagePreview changes to prevent memory leaks
  React.useEffect(() => {
    return () => {
      if (coverImagePreview?.startsWith('blob:')) {
        URL.revokeObjectURL(coverImagePreview);
      }
    };
  }, [coverImagePreview]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validation = validateFile(file, 'image');
    if (!validation.isValid) {
      setFileError(validation.error);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setFileError(null);
    if (coverImagePreview?.startsWith('blob:')) URL.revokeObjectURL(coverImagePreview);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setCoverImageFile(file);
    setCoverImagePreview(URL.createObjectURL(file));
    setCoverRemoved(false);
  };

  const handleRemoveCover = () => {
    if (fileInputRef.current) fileInputRef.current.value = '';
    setCoverImageFile(null);
    setCoverImagePreview(null);
    setCoverRemoved(true);
    setFileError(null);
  };

  const handleSubmit = () => {
    onSubmit({ form, coverImageFile, coverRemoved, originalCoverImage: channel.cover_image });
  };

  return (
    <div
      className="modal-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="modal-content large edit-channel-modal">
        <div className="modal-header">
          <h3>Edit Channel</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>

        <div className="modal-body">
          {/* Cover Image */}
          <div className="form-group">
            <label>Cover Image</label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            {coverImagePreview ? (
              <div className="cover-image-picker cover-image-picker--has-preview">
                <img src={coverImagePreview} alt="Cover preview" className="cover-image-preview" />
                <div className="cover-image-picker-actions">
                  <button
                    type="button"
                    className="cover-image-btn"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload size={13} /> Change
                  </button>
                  <button
                    type="button"
                    className="cover-image-btn cover-image-btn--remove"
                    onClick={handleRemoveCover}
                  >
                    <X size={13} /> Remove
                  </button>
                </div>
              </div>
            ) : (
              <div
                className="cover-image-picker"
                onClick={() => fileInputRef.current?.click()}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
              >
                <Image size={22} color="var(--admin-gray-400)" />
                <span className="cover-image-picker-label">Click to upload cover image</span>
                <span className="cover-image-picker-hint">JPG, PNG, WebP · max 10 MB</span>
              </div>
            )}
            {fileError && (
              <div className="form-error" style={{ marginTop: 8 }}>
                <AlertCircle size={15} />
                {fileError}
              </div>
            )}
          </div>

          {/* Name */}
          <div className="form-group">
            <label>Name <span className="required">*</span></label>
            <input
              type="text"
              className="form-input"
              maxLength={100}
              value={form.name}
              onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
            />
          </div>

          {/* Description */}
          <div className="form-group">
            <label>Description</label>
            <textarea
              className="form-textarea"
              maxLength={500}
              value={form.description}
              onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
            />
          </div>

          {/* Type + Privacy */}
          <div className="form-row">
            <div className="form-group">
              <label>Type</label>
              <select
                className="form-select"
                value={form.channel_type}
                onChange={(e) => setForm(prev => ({ ...prev, channel_type: e.target.value }))}
              >
                <option value="interest">Interest</option>
                <option value="location">Location</option>
                <option value="organization">Organization</option>
              </select>
            </div>
            <div className="form-group">
              <label>Privacy</label>
              <select
                className="form-select"
                value={form.privacy}
                onChange={(e) => setForm(prev => ({ ...prev, privacy: e.target.value }))}
              >
                <option value="public">Public</option>
                <option value="private">Private</option>
                <option value="invite_only">Invite Only</option>
              </select>
            </div>
          </div>

          {/* Location fields (only when type = location) */}
          {form.channel_type === 'location' && (
            <div className="form-row">
              <div className="form-group">
                <label>City</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="City"
                  value={form.location_city}
                  onChange={(e) => setForm(prev => ({ ...prev, location_city: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Region / State</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Region or state"
                  value={form.location_region}
                  onChange={(e) => setForm(prev => ({ ...prev, location_region: e.target.value }))}
                />
              </div>
            </div>
          )}

          {/* Tags */}
          <div className="form-group">
            <label>Tags</label>
            <input
              type="text"
              className="form-input"
              placeholder="Comma-separated tags"
              value={form.tags}
              onChange={(e) => setForm(prev => ({ ...prev, tags: e.target.value }))}
            />
          </div>

          {/* Featured */}
          <div className="form-group">
            <label className="form-checkbox-group">
              <input
                type="checkbox"
                checked={form.is_featured}
                onChange={(e) => setForm(prev => ({ ...prev, is_featured: e.target.checked }))}
              />
              <div>
                <div className="checkbox-label">Featured channel</div>
                <div className="checkbox-hint">Appears in featured channel lists in the app</div>
              </div>
            </label>
          </div>

          {/* Platform created */}
          <div className="form-group">
            <label className="form-checkbox-group">
              <input
                type="checkbox"
                checked={form.is_platform_created}
                onChange={(e) => setForm(prev => ({ ...prev, is_platform_created: e.target.checked }))}
              />
              <div>
                <div className="checkbox-label">Created by VibrantCast</div>
                <div className="checkbox-hint">
                  Shows "Created by VibrantCast" publicly instead of the owner's profile.
                </div>
              </div>
            </label>
          </div>

          {error && (
            <div className="form-error">
              <AlertCircle size={15} />
              {error}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-cancel" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button className="btn-submit" onClick={handleSubmit} disabled={loading}>
            {loading ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── ChannelsAdmin (main component) ──────────────────────────────────────────

const ChannelsAdmin = () => {
  // ─── Main state ──────────────────────────────────────────────────────────────
  const [channels, setChannels] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [channelMembers, setChannelMembers] = useState([]);
  const [channelPosts, setChannelPosts] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ─── Tab state ────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('list');
  const [detailTab, setDetailTab] = useState('overview');

  // ─── Filter + pagination ──────────────────────────────────────────────────────
  const [filters, setFilters] = useState({
    search: '',
    channel_type: '',
    privacy: '',
    is_active: ''
  });
  const [pagination, setPagination] = useState({ offset: 0, limit: 20, total: 0 });
  const [postsPagination, setPostsPagination] = useState({ offset: 0, limit: 20, total: 0 });

  // ─── Create modal state ───────────────────────────────────────────────────────
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [ownerSearchQuery, setOwnerSearchQuery] = useState('');
  const [ownerSearchResults, setOwnerSearchResults] = useState([]);
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [createForm, setCreateForm] = useState({
    name: '',
    description: '',
    channel_type: 'interest',
    privacy: 'public',
    location_city: '',
    location_region: '',
    tags: '',
    is_featured: false,
    is_platform_created: false
  });
  const [coverImageFile, setCoverImageFile] = useState(null);
  const [coverImagePreview, setCoverImagePreview] = useState(null);

  // ─── Edit modal state ─────────────────────────────────────────────────────────
  const [showEditModal, setShowEditModal] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState(null);

  const searchDebounceRef = useRef(null);
  const ownerSearchDebounceRef = useRef(null);

  // ─── Data loading ─────────────────────────────────────────────────────────────
  const loadChannels = useCallback(async (currentFilters, currentPagination) => {
    try {
      setLoading(true);
      setError(null);
      const result = await getAllChannels(
        currentFilters,
        currentPagination.limit,
        currentPagination.offset
      );
      setChannels(result.channels);
      setPagination(prev => ({ ...prev, total: result.total }));
    } catch (err) {
      setError(err.message || 'Failed to load channels');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAnalytics = useCallback(async () => {
    try {
      const data = await getChannelAnalytics();
      setAnalytics(data);
    } catch (err) {
      console.error('Failed to load analytics:', err);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadChannels(filters, pagination);
    loadAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reload when pagination offset changes
  useEffect(() => {
    loadChannels(filters, pagination);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.offset]);

  // Debounce filter changes
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      const resetPagination = { ...pagination, offset: 0 };
      setPagination(resetPagination);
      loadChannels(filters, resetPagination);
    }, 300);
    return () => clearTimeout(searchDebounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search, filters.channel_type, filters.privacy, filters.is_active]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleRefresh = () => {
    const resetPagination = { ...pagination, offset: 0 };
    setPagination(resetPagination);
    loadChannels(filters, resetPagination);
    loadAnalytics();
  };

  // ─── Channel detail ──────────────────────────────────────────────────────────
  const openChannelDetail = async (channel) => {
    setSelectedChannel(channel);
    setDetailTab('overview');
    try {
      const [membersResult, postsResult] = await Promise.all([
        getChannelMembers(channel.id, 50, 0),
        getChannelPosts(channel.id, 20, 0)
      ]);
      setChannelMembers(membersResult.members);
      setChannelPosts(postsResult.posts);
      setPostsPagination({ offset: 0, limit: 20, total: postsResult.total });
    } catch (err) {
      console.error('Error loading channel detail data:', err);
    }
  };

  const loadMorePosts = async (newOffset) => {
    if (!selectedChannel) return;
    try {
      const result = await getChannelPosts(selectedChannel.id, 20, newOffset);
      setChannelPosts(result.posts);
      setPostsPagination(prev => ({ ...prev, offset: newOffset, total: result.total }));
    } catch (err) {
      console.error('Error loading posts:', err);
    }
  };

  // ─── Feature / active toggles ─────────────────────────────────────────────────
  const handleToggleFeatured = async (channel) => {
    try {
      await featureChannel(channel.id, !channel.is_featured);
      setChannels(prev =>
        prev.map(c => c.id === channel.id ? { ...c, is_featured: !c.is_featured } : c)
      );
      if (selectedChannel?.id === channel.id) {
        setSelectedChannel(prev => ({ ...prev, is_featured: !prev.is_featured }));
      }
    } catch (err) {
      alert('Failed to update featured status: ' + err.message);
    }
  };

  const handleToggleActive = async (channel) => {
    const action = channel.is_active ? 'deactivate' : 'activate';
    if (!window.confirm(`Are you sure you want to ${action} "${channel.name}"?`)) return;
    try {
      await toggleChannelActive(channel.id, !channel.is_active);
      loadChannels(filters, pagination);
      if (selectedChannel?.id === channel.id) {
        setSelectedChannel(null);
      }
    } catch (err) {
      alert('Failed to update active status: ' + err.message);
    }
  };

  // ─── Post moderation ──────────────────────────────────────────────────────────
  const handleModeratePost = async (post) => {
    const action = post.is_active ? 'hide' : 'unhide';
    try {
      await moderatePost(post.id, action);
      setChannelPosts(prev =>
        prev.map(p => p.id === post.id ? { ...p, is_active: !p.is_active } : p)
      );
    } catch (err) {
      alert('Failed to moderate post: ' + err.message);
    }
  };

  // ─── Owner search ─────────────────────────────────────────────────────────────
  const handleOwnerSearchChange = (value) => {
    setOwnerSearchQuery(value);
    if (ownerSearchDebounceRef.current) clearTimeout(ownerSearchDebounceRef.current);
    if (!value.trim()) {
      setOwnerSearchResults([]);
      return;
    }
    ownerSearchDebounceRef.current = setTimeout(async () => {
      try {
        const results = await searchUsers(value);
        setOwnerSearchResults(results);
      } catch (err) {
        console.error('Owner search failed:', err);
      }
    }, 300);
  };

  const selectOwner = (user) => {
    setSelectedOwner(user);
    setOwnerSearchQuery('');
    setOwnerSearchResults([]);
  };

  const clearOwner = () => {
    setSelectedOwner(null);
    setOwnerSearchQuery('');
    setOwnerSearchResults([]);
  };

  // ─── Create channel ──────────────────────────────────────────────────────────
  const resetCreateForm = () => {
    setCreateForm({
      name: '',
      description: '',
      channel_type: 'interest',
      privacy: 'public',
      location_city: '',
      location_region: '',
      tags: '',
      is_featured: false,
      is_platform_created: false
    });
    setSelectedOwner(null);
    setOwnerSearchQuery('');
    setOwnerSearchResults([]);
    setCreateError(null);
    if (coverImagePreview?.startsWith('blob:')) URL.revokeObjectURL(coverImagePreview);
    setCoverImageFile(null);
    setCoverImagePreview(null);
  };

  const handleFormChange = (key, value) => {
    setCreateForm(prev => ({ ...prev, [key]: value }));
  };

  const handleCoverImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validation = validateFile(file, 'image');
    if (!validation.isValid) {
      setCreateError(validation.error);
      if (e.target) e.target.value = '';
      return;
    }
    if (coverImagePreview?.startsWith('blob:')) URL.revokeObjectURL(coverImagePreview);
    if (e.target) e.target.value = '';
    setCoverImageFile(file);
    setCoverImagePreview(URL.createObjectURL(file));
  };

  const handleCoverImageRemove = () => {
    setCoverImageFile(null);
    setCoverImagePreview(null);
  };

  const handleCloseModal = () => {
    setShowCreateModal(false);
    resetCreateForm();
  };

  const handleCreateSubmit = async () => {
    if (!createForm.name.trim()) {
      setCreateError('Channel name is required.');
      return;
    }
    if (!selectedOwner) {
      setCreateError('An owner must be selected.');
      return;
    }
    setCreateLoading(true);
    setCreateError(null);
    try {
      const tagsArray = createForm.tags
        ? createForm.tags.split(',').map(t => t.trim()).filter(Boolean)
        : [];

      const channel = await createChannel({
        name: createForm.name.trim(),
        created_by: selectedOwner.uuid,
        description: createForm.description.trim() || null,
        channel_type: createForm.channel_type,
        privacy: createForm.privacy,
        location_city: createForm.channel_type === 'location' ? createForm.location_city.trim() || null : null,
        location_region: createForm.channel_type === 'location' ? createForm.location_region.trim() || null : null,
        tags: tagsArray,
        is_featured: createForm.is_featured,
        is_platform_created: createForm.is_platform_created
      });

      // Upload cover image now that we have the channel ID for the storage path
      if (coverImageFile) {
        let uploadedPath = null;
        try {
          const { url, path } = await uploadChannelImage(coverImageFile, channel.id);
          uploadedPath = path;
          await updateChannel(channel.id, { cover_image: url });
        } catch (imgErr) {
          console.error('Cover image upload failed (channel was still created):', imgErr);
          if (uploadedPath) {
            try { await deleteChannelImage(uploadedPath); } catch (_) {}
          }
        }
      }

      handleCloseModal();
      const resetPagination = { ...pagination, offset: 0 };
      setPagination(resetPagination);
      loadChannels(filters, resetPagination);
      loadAnalytics();
      alert(`Channel "${createForm.name}" created successfully.`);
    } catch (err) {
      setCreateError(err.message || 'Failed to create channel.');
    } finally {
      setCreateLoading(false);
    }
  };

  // ─── Edit channel ─────────────────────────────────────────────────────────────
  const handleOpenEdit = () => {
    setEditError(null);
    setShowEditModal(true);
  };

  const handleCloseEdit = () => {
    setShowEditModal(false);
    setEditError(null);
  };

  const handleEditSubmit = async ({ form, coverImageFile: newImageFile, coverRemoved, originalCoverImage }) => {
    if (!form.name.trim()) {
      setEditError('Channel name is required.');
      return;
    }
    setEditLoading(true);
    setEditError(null);
    try {
      let coverImageUrl = originalCoverImage ?? null;

      if (newImageFile) {
        // Upload first — only delete old image after new one is confirmed
        const { url } = await uploadChannelImage(newImageFile, selectedChannel.id);
        coverImageUrl = url;
        if (originalCoverImage) {
          try { await deleteChannelImage(originalCoverImage); } catch (e) { console.error(e); }
        }
      } else if (coverRemoved) {
        coverImageUrl = null;
        if (originalCoverImage) {
          try { await deleteChannelImage(originalCoverImage); } catch (e) { console.error(e); }
        }
      }

      const tagsArray = form.tags
        ? form.tags.split(',').map(t => t.trim()).filter(Boolean)
        : [];

      const updated = await updateChannel(selectedChannel.id, {
        name: form.name.trim(),
        description: form.description.trim() || null,
        channel_type: form.channel_type,
        privacy: form.privacy,
        location_city: form.channel_type === 'location' ? form.location_city.trim() || null : null,
        location_region: form.channel_type === 'location' ? form.location_region.trim() || null : null,
        tags: tagsArray,
        is_featured: form.is_featured,
        is_platform_created: form.is_platform_created,
        cover_image: coverImageUrl
      });

      setSelectedChannel(updated);
      setChannels(prev => prev.map(c => c.id === updated.id ? { ...c, ...updated } : c));
      handleCloseEdit();
    } catch (err) {
      setEditError(err.message || 'Failed to save changes.');
    } finally {
      setEditLoading(false);
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────────
  const currentPage = Math.floor(pagination.offset / pagination.limit) + 1;
  const totalPages = Math.ceil(pagination.total / pagination.limit);

  return (
    <div className="channels-admin">
      <h1>Channels</h1>
      <p className="page-subtitle">Manage channels, members, and posts across the platform.</p>

      {/* Top-level tabs */}
      <div className="channels-tab-navigation">
        <button
          className={activeTab === 'list' ? 'active' : ''}
          onClick={() => setActiveTab('list')}
        >
          <Users size={15} /> Channels
        </button>
        <button
          className={activeTab === 'analytics' ? 'active' : ''}
          onClick={() => setActiveTab('analytics')}
        >
          <BarChart3 size={15} /> Analytics
        </button>
      </div>

      {/* Error banner */}
      {error && (
        <div className="channels-error">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {/* Analytics tab */}
      {activeTab === 'analytics' && <AnalyticsTab analytics={analytics} />}

      {/* List tab */}
      {activeTab === 'list' && (
        <>
          {selectedChannel ? (
            <ChannelDetailView
              channel={selectedChannel}
              members={channelMembers}
              posts={channelPosts}
              postsPagination={postsPagination}
              detailTab={detailTab}
              onBack={() => setSelectedChannel(null)}
              onTabChange={setDetailTab}
              onToggleFeatured={handleToggleFeatured}
              onToggleActive={handleToggleActive}
              onModeratePost={handleModeratePost}
              onLoadMorePosts={loadMorePosts}
              onEdit={handleOpenEdit}
            />
          ) : (
            <>
              {/* Filter bar */}
              <div className="channels-filter-bar">
                <div className="search-wrapper">
                  <Search size={15} />
                  <input
                    type="text"
                    placeholder="Search channels…"
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                  />
                </div>

                <select
                  value={filters.channel_type}
                  onChange={(e) => handleFilterChange('channel_type', e.target.value)}
                >
                  <option value="">All Types</option>
                  <option value="interest">Interest</option>
                  <option value="location">Location</option>
                  <option value="organization">Organization</option>
                </select>

                <select
                  value={filters.privacy}
                  onChange={(e) => handleFilterChange('privacy', e.target.value)}
                >
                  <option value="">All Privacy</option>
                  <option value="public">Public</option>
                  <option value="private">Private</option>
                  <option value="invite_only">Invite Only</option>
                </select>

                <select
                  value={filters.is_active}
                  onChange={(e) => handleFilterChange('is_active', e.target.value)}
                >
                  <option value="">All Status</option>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>

                <button className="btn-icon" onClick={handleRefresh}>
                  <RefreshCw size={15} /> Refresh
                </button>

                <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
                  <Plus size={15} /> Create Channel
                </button>
              </div>

              <div className="channels-results-count">
                Showing {channels.length} of {pagination.total} channels
              </div>

              {/* Loading state */}
              {loading ? (
                <div className="channels-loading">
                  <div className="loading-spinner" />
                  <span>Loading channels…</span>
                </div>
              ) : channels.length === 0 ? (
                <div className="empty-state">
                  <Users size={40} />
                  <p>No channels found matching your filters.</p>
                </div>
              ) : (
                <>
                  <div style={{ overflowX: 'auto' }}>
                    <table className="channels-table">
                      <thead>
                        <tr>
                          <th>Channel</th>
                          <th>Type</th>
                          <th>Privacy</th>
                          <th>Members</th>
                          <th>Creator</th>
                          <th>Created</th>
                          <th>Featured</th>
                          <th>Active</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {channels.map(channel => (
                          <tr key={channel.id}>
                            <td>
                              <div className="channel-name-cell">
                                <div className="channel-thumb">
                                  {channel.cover_image
                                    ? <img src={channel.cover_image} alt={channel.name} />
                                    : <Image size={18} />
                                  }
                                </div>
                                <div>
                                  <div className="channel-name">{channel.name}</div>
                                  <div className="channel-slug">/{channel.slug}</div>
                                </div>
                              </div>
                            </td>
                            <td>
                              <span className={`type-badge ${channel.channel_type}`}>
                                {channel.channel_type}
                              </span>
                            </td>
                            <td>
                              <span className={`privacy-badge ${channel.privacy}`}>
                                <PrivacyIcon privacy={channel.privacy} /> {channel.privacy.replace('_', ' ')}
                              </span>
                            </td>
                            <td>{channel.member_count ?? 0}</td>
                            <td><CreatorDisplay channel={channel} /></td>
                            <td style={{ fontSize: '0.82rem', color: 'var(--admin-gray-500)', whiteSpace: 'nowrap' }}>
                              {formatDate(channel.created_at)}
                            </td>
                            <td>
                              <button
                                className={`btn-icon-sm ${channel.is_featured ? 'active' : ''}`}
                                onClick={() => handleToggleFeatured(channel)}
                                title={channel.is_featured ? 'Unfeature' : 'Feature'}
                              >
                                <Star size={15} fill={channel.is_featured ? 'currentColor' : 'none'} />
                              </button>
                            </td>
                            <td>
                              <button
                                className="btn-icon-sm"
                                onClick={() => handleToggleActive(channel)}
                                title={channel.is_active ? 'Deactivate' : 'Activate'}
                                style={{ color: channel.is_active ? 'var(--admin-success)' : 'var(--admin-gray-400)' }}
                              >
                                {channel.is_active ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                              </button>
                            </td>
                            <td>
                              <div className="row-actions">
                                <button
                                  className="btn-view"
                                  onClick={() => openChannelDetail(channel)}
                                >
                                  <Eye size={13} style={{ marginRight: 3 }} />View
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {pagination.total > pagination.limit && (
                    <div className="channels-pagination">
                      <span>
                        Page {currentPage} of {totalPages} ({pagination.total} total)
                      </span>
                      <button
                        disabled={pagination.offset === 0}
                        onClick={() => setPagination(prev => ({ ...prev, offset: prev.offset - prev.limit }))}
                      >
                        Previous
                      </button>
                      <button
                        disabled={pagination.offset + pagination.limit >= pagination.total}
                        onClick={() => setPagination(prev => ({ ...prev, offset: prev.offset + prev.limit }))}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}

      {/* Create Channel Modal */}
      {showCreateModal && (
        <CreateChannelModal
          ownerSearchQuery={ownerSearchQuery}
          ownerSearchResults={ownerSearchResults}
          selectedOwner={selectedOwner}
          createForm={createForm}
          createLoading={createLoading}
          createError={createError}
          coverImagePreview={coverImagePreview}
          onOwnerSearchChange={handleOwnerSearchChange}
          onSelectOwner={selectOwner}
          onClearOwner={clearOwner}
          onFormChange={handleFormChange}
          onCoverImageChange={handleCoverImageChange}
          onCoverImageRemove={handleCoverImageRemove}
          onSubmit={handleCreateSubmit}
          onClose={handleCloseModal}
        />
      )}

      {/* Edit Channel Modal */}
      {showEditModal && selectedChannel && (
        <EditChannelModal
          key={selectedChannel.id}
          channel={selectedChannel}
          loading={editLoading}
          error={editError}
          onSubmit={handleEditSubmit}
          onClose={handleCloseEdit}
        />
      )}
    </div>
  );
};

export default ChannelsAdmin;
